import { useState, useCallback, useEffect, useRef } from 'react';
import type { IntakeResponse } from '../services/api';
import type { IntakeRecord } from '../store/slices/intakeSlice';

export type SaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';

/** Default debounce window for `debouncedSave`; also the pill's countdown. */
export const INTAKE_FIELD_SAVE_DEBOUNCE_MS = 1500;

interface UseIntakeFieldSaveOptions {
  intakeId: string | undefined;
  updateIntakeRecord: (
    id: string,
    data: Partial<IntakeResponse>
  ) => Promise<IntakeRecord>;
  /** Called after a successful save (e.g. to reload relationships). */
  onAfterSave?: () => Promise<void> | void;
}

interface UseIntakeFieldSaveReturn {
  saveStatus: SaveStatus;
  /** Increments on each debounced edit so a countdown UI can restart in sync. */
  changeToken: number;
  /** Save a single field immediately. Returns false when the PATCH fails. */
  saveField: (fieldName: string, value: unknown) => Promise<boolean>;
  /** Save multiple fields immediately. Returns false when the PATCH fails. */
  saveBatch: (updates: Record<string, unknown>) => Promise<boolean>;
  /** Schedule a debounced save for a field. */
  debouncedSave: (fieldName: string, value: unknown, delay?: number) => void;
  /** Cancel a pending debounced save for a field. */
  cancelDebounce: (fieldName: string) => void;
}

/**
 * Manages save-status indicators and debounced/immediate field saves for
 * intake forms. Replaces the duplicated saveStatus + saveTimeoutRef +
 * debounceTimeoutsRef boilerplate in EligibilityCheckStep, PriorAuthStep,
 * DynamicIntakeForm, etc.
 */
export function useIntakeFieldSave({
  intakeId,
  updateIntakeRecord,
  onAfterSave,
}: UseIntakeFieldSaveOptions): UseIntakeFieldSaveReturn {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  // Bumps on each debounced edit so a countdown bar restarts in sync.
  const [changeToken, setChangeToken] = useState(0);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});
  // Synchronous mirror of changeToken: each debounced edit bumps it, and a save
  // captures it at start so a stale completion can't report "saved" over a
  // newer queued edit.
  const editGenerationRef = useRef(0);

  // Keep onAfterSave in a ref so it doesn't cause re-creation of callbacks.
  const onAfterSaveRef = useRef(onAfterSave);
  onAfterSaveRef.current = onAfterSave;

  // Cancel pending debounced saves when the intake changes so a queued
  // save from the previous intake never fires against the new one.
  useEffect(() => {
    Object.values(debounceTimeoutsRef.current).forEach(clearTimeout);
    debounceTimeoutsRef.current = {};

    return () => {
      Object.values(debounceTimeoutsRef.current).forEach(clearTimeout);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [intakeId]);

  const setSaved = useCallback(() => {
    setSaveStatus('saved');
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    // Only fall back to idle if a newer edit hasn't already moved on.
    saveTimeoutRef.current = setTimeout(
      () => setSaveStatus(s => (s === 'saved' ? 'idle' : s)),
      2000
    );
  }, []);

  const setError = useCallback(() => {
    setSaveStatus('error');
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(
      () => setSaveStatus(s => (s === 'error' ? 'idle' : s)),
      3000
    );
  }, []);

  const saveBatch = useCallback(
    async (updates: Record<string, unknown>): Promise<boolean> => {
      if (!intakeId) return false;
      // Tie this save to the latest edit; if a newer debounced edit arrives
      // while it is in flight, suppress the "saved" transition so the pill
      // keeps reflecting the still-pending edit.
      const savingGeneration = editGenerationRef.current;
      try {
        setSaveStatus('saving');
        await updateIntakeRecord(intakeId, updates as Partial<IntakeResponse>);
        if (onAfterSaveRef.current) await onAfterSaveRef.current();
        if (editGenerationRef.current === savingGeneration) setSaved();
        return true;
      } catch (err) {
        console.error('Failed to save:', err);
        setError();
        return false;
      }
    },
    [intakeId, updateIntakeRecord, setSaved, setError]
  );

  const saveField = useCallback(
    async (fieldName: string, value: unknown): Promise<boolean> => {
      return saveBatch({ [fieldName]: value });
    },
    [saveBatch]
  );

  const debouncedSave = useCallback(
    (
      fieldName: string,
      value: unknown,
      delay = INTAKE_FIELD_SAVE_DEBOUNCE_MS
    ) => {
      if (debounceTimeoutsRef.current[fieldName]) {
        clearTimeout(debounceTimeoutsRef.current[fieldName]);
      }
      // Surface the pending/countdown state while the debounce window runs;
      // bump the token so the countdown bar restarts on each edit, and bump the
      // generation so an in-flight save won't later report this edit as saved.
      setSaveStatus('unsaved');
      setChangeToken(t => t + 1);
      editGenerationRef.current += 1;
      debounceTimeoutsRef.current[fieldName] = setTimeout(() => {
        void saveField(fieldName, value);
        delete debounceTimeoutsRef.current[fieldName];
      }, delay);
    },
    [saveField]
  );

  const cancelDebounce = useCallback((fieldName: string) => {
    if (debounceTimeoutsRef.current[fieldName]) {
      clearTimeout(debounceTimeoutsRef.current[fieldName]);
      delete debounceTimeoutsRef.current[fieldName];
    }
    // Drop a lingering pending state once nothing else is queued (e.g. a blur
    // that cancels the debounce without a value change).
    if (Object.keys(debounceTimeoutsRef.current).length === 0) {
      setSaveStatus(s => (s === 'unsaved' ? 'idle' : s));
    }
  }, []);

  return {
    saveStatus,
    changeToken,
    saveField,
    saveBatch,
    debouncedSave,
    cancelDebounce,
  };
}
